import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
  emailAutomationService,
  type EmailHistoryEntry,
  type EmailFlow,
  type EmailFlowRecipient,
  type EmailFlowTrigger,
  type EmailTemplate,
} from '../../services/emailAutomationService';
import {
  adminSubscriptionReminderService,
  type SubscriptionReminderOverview,
  type SubscriptionReminderSettings,
} from '../../services/adminSubscriptionReminderService';

type PostmarkOverview = {
  postmarkConfigured: boolean;
  serverId: string;
  defaultMessageStream: string;
  streams: Array<{ ID: string; Name: string; MessageStreamType: string }>;
  templatesCount: number;
  flowsCount: number;
  activeFlowsCount: number;
  remoteTemplatesCount: number;
  openSessionsCount: number;
  paidSessionsCount: number;
};

type EmailTestSuiteResult = {
  alias: string;
  name: string;
  status: 'sent' | 'failed';
  messageId?: string;
  error?: string;
};

type EmailTestSuitePayload = {
  ok: boolean;
  recipientEmail: string;
  summary: {
    sent: number;
    failed: number;
    tested: number;
  };
  results: EmailTestSuiteResult[];
};

type SubscriptionReminderResult = {
  status: string;
  subscriptionNumber?: string;
  recipientEmail?: string;
  daysBefore?: number;
  targetDate?: string;
  templateAlias?: string;
  messageId?: string;
  error?: string;
};

type SubscriptionReminderReport = {
  dryRun: boolean;
  templateAlias: string;
  reminderDays: number[];
  stats: {
    checked: number;
    eligible: number;
    sent: number;
    failed: number;
    skippedAlreadySent: number;
    skippedNoEmail: number;
  };
  results: SubscriptionReminderResult[];
};

type SubscriptionReminderTestPayload = {
  templateAlias: string;
  recipientEmail: string;
  subscriptionNumber: string;
  messageId: string;
};

type SubscriptionReminderPayload = {
  ok: boolean;
  action: 'preview' | 'run' | 'test';
  report?: SubscriptionReminderReport;
  result?: SubscriptionReminderTestPayload;
  overview?: SubscriptionReminderOverview;
};

const triggerLabels: Record<EmailFlowTrigger, string> = {
  payment_paid: 'Paiement confirme',
  payment_pending: 'Paiement en attente',
  checkout_abandoned: 'Panier abandonne',
  payment_failed: 'Paiement echoue',
};

const recipientLabels: Record<EmailFlowRecipient, string> = {
  customer: 'Client',
  admin: 'Admin',
};

const emailStatusBadge = (status: string) => {
  const normalized = status.toLowerCase();

  if (normalized.includes('sent') || normalized.includes('delivered')) {
    return 'bg-emerald-100 text-emerald-700';
  }

  if (normalized.includes('open') || normalized.includes('click')) {
    return 'bg-blue-100 text-blue-700';
  }

  if (
    normalized.includes('error') ||
    normalized.includes('bounce') ||
    normalized.includes('blocked') ||
    normalized.includes('spam') ||
    normalized.includes('fail')
  ) {
    return 'bg-red-100 text-red-700';
  }

  return 'bg-gray-100 text-gray-700';
};

const blankOverview: PostmarkOverview = {
  postmarkConfigured: false,
  serverId: '',
  defaultMessageStream: 'checkout-automation',
  streams: [],
  templatesCount: 0,
  flowsCount: 0,
  activeFlowsCount: 0,
  remoteTemplatesCount: 0,
  openSessionsCount: 0,
  paidSessionsCount: 0,
};

const createTemplateKey = () => `custom_template_${Date.now()}`;
const createFlowKey = () => `custom_flow_${Date.now()}`;
const CUSTOM_TEMPLATE_ALIAS = 'csd-custom-modular';

const createEmptyTemplate = (): EmailTemplate => ({
  templateKey: createTemplateKey(),
  name: 'Nouveau template',
  alias: `custom-template-${Date.now()}`,
  subject: 'Sujet du template',
  htmlBody: '<html><body><h1>Bonjour {{client_name}}</h1><p>Votre message ici.</p></body></html>',
  textBody: 'Bonjour {{client_name}}, votre message ici.',
  messageStream: 'checkout-automation',
  fromEmail: 'contact@couteausuissedigital.com',
  replyTo: 'contact@couteausuissedigital.com',
  active: true,
  sampleModel: {
    client_name: 'Jean Dupont',
  },
});

const createCustomEmailTemplate = (baseTemplate?: EmailTemplate): EmailTemplate => {
  const timestamp = Date.now();
  const base = baseTemplate || createEmptyTemplate();

  return {
    ...base,
    id: undefined,
    postmarkTemplateId: undefined,
    lastSyncedAt: '',
    templateKey: `custom_email_${timestamp}`,
    name: 'Email personnalisé',
    alias: `custom-email-${timestamp}`,
    subject: 'Votre message personnalisé — Couteau Suisse Digital',
    active: true,
    sampleModel: {
      ...base.sampleModel,
      email_title: 'Votre message personnalisé',
      email_label: 'Message sur mesure',
      hero_title: 'Un email conçu pour votre client',
      hero_text:
        'Bonjour {{customer_first_name}}, nous vous envoyons ce message personnalisé concernant votre projet et la prochaine étape.',
      primary_button_text: 'Voir les détails',
      section_title: 'Message principal',
      section_text:
        'Ajoutez ici le contenu principal de votre email personnalisé.',
      note_title: 'Important',
      note_text:
        'Ajoutez ici une note importante, un rappel ou une instruction spécifique.',
    },
  };
};

const createEmptyFlow = (templateAlias = ''): EmailFlow => ({
  flowKey: createFlowKey(),
  name: 'Nouveau flow',
  description: 'Nouveau scenario email.',
  triggerType: 'payment_pending',
  recipientType: 'customer',
  templateAlias,
  messageStream: 'checkout-automation',
  delayMinutes: 60,
  maxSends: 1,
  enabled: true,
});

type EmailManagerProps = {
  onOpenSettings?: () => void;
};

const EmailManager = ({ onOpenSettings }: EmailManagerProps) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [flows, setFlows] = useState<EmailFlow[]>([]);
  const [overview, setOverview] = useState<PostmarkOverview>(blankOverview);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<EmailFlow | null>(null);
  const [history, setHistory] = useState<EmailHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [testRecipientEmail, setTestRecipientEmail] = useState('admin@couteausuissedigital.com');
  const [isSendingSuite, setIsSendingSuite] = useState(false);
  const [suitePayload, setSuitePayload] = useState<EmailTestSuitePayload | null>(null);
  const [reminderAction, setReminderAction] = useState<'preview' | 'run' | 'test' | null>(null);
  const [reminderPayload, setReminderPayload] = useState<SubscriptionReminderPayload | null>(null);
  const [reminderOverview, setReminderOverview] = useState<SubscriptionReminderOverview | null>(
    null
  );
  const [reminderSettingsDraft, setReminderSettingsDraft] =
    useState<SubscriptionReminderSettings | null>(null);
  const [isSavingReminderSettings, setIsSavingReminderSettings] = useState(false);
  const modularTemplate = templates.find((template) => template.alias === CUSTOM_TEMPLATE_ALIAS);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await emailAutomationService.ensureDefaults();

      const [templatesData, flowsData, overviewResponse, historyData, reminderOverviewData] = await Promise.all([
        emailAutomationService.getEmailTemplates(),
        emailAutomationService.getEmailFlows(),
        fetch('/api/postmark/overview', {
          credentials: 'include',
          cache: 'no-store',
        }),
        emailAutomationService.getEmailHistory(120).catch((error) => {
          console.error(error);
          return [];
        }),
        adminSubscriptionReminderService.getOverview().catch((error) => {
          console.error(error);
          return null;
        }),
      ]);

      const overviewPayload = await overviewResponse.json().catch(() => blankOverview);
      if (!overviewResponse.ok) {
        throw new Error(overviewPayload?.error || 'Impossible de charger la connexion Postmark');
      }

      setTemplates(templatesData);
      setFlows(flowsData);
      setHistory(historyData);
      setOverview(overviewPayload);
      setReminderOverview(reminderOverviewData);
      setReminderSettingsDraft(reminderOverviewData?.settings || null);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erreur de chargement email');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData().catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!selectedTemplate && !selectedFlow) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedTemplate(null);
        setSelectedFlow(null);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedTemplate, selectedFlow]);

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) {
      return;
    }

    if (!selectedTemplate.templateKey.trim() || !selectedTemplate.alias.trim()) {
      toast.error('Template key et alias sont obligatoires');
      return;
    }

    try {
      await emailAutomationService.saveEmailTemplate(selectedTemplate);
      await loadData();
      toast.success('Template sauvegarde !');
      setSelectedTemplate(null);
    } catch (error) {
      console.error(error);
      toast.error('Impossible de sauvegarder ce template');
    }
  };

  const handleSaveFlow = async () => {
    if (!selectedFlow) {
      return;
    }

    if (!selectedFlow.flowKey.trim() || !selectedFlow.templateAlias.trim()) {
      toast.error('Flow key et template sont obligatoires');
      return;
    }

    try {
      await emailAutomationService.saveEmailFlow(selectedFlow);
      await loadData();
      toast.success('Flow sauvegarde !');
      setSelectedFlow(null);
    } catch (error) {
      console.error(error);
      toast.error('Impossible de sauvegarder ce flow');
    }
  };

  const handleSyncPostmark = async () => {
    setIsSyncing(true);
    try {
      const response = await fetch('/api/postmark/sync', {
        method: 'POST',
        credentials: 'include',
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Synchronisation Postmark impossible');
      }

      toast.success(`${payload?.synced || 0} templates synchronises avec Postmark`);
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erreur de synchronisation');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSendTest = async (templateAlias: string) => {
    const recipientEmail = window.prompt(
      'Email de destination pour le test Postmark',
      'admin@couteausuissedigital.com'
    );

    if (!recipientEmail) {
      return;
    }

    try {
      const response = await fetch('/api/postmark/send-test', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateAlias,
          recipientEmail,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Envoi de test impossible');
      }

      toast.success('Email de test envoye !');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erreur de test send');
    }
  };

  const handleOpenPostmarkTemplate = (template: EmailTemplate) => {
    if (!overview.postmarkConfigured || !overview.serverId.trim()) {
      toast.error('Configure Postmark dans Settings avant d ouvrir un template');
      return;
    }

    const serverId = encodeURIComponent(overview.serverId.trim());
    const templateId = Number(template.postmarkTemplateId || 0);
    const postmarkUrl =
      templateId > 0
        ? `https://account.postmarkapp.com/servers/${serverId}/templates/${templateId}`
        : `https://account.postmarkapp.com/servers/${serverId}/templates`;

    window.open(postmarkUrl, '_blank', 'noopener,noreferrer');
  };

  const handleRunEmailSuite = async () => {
    const recipientEmail = testRecipientEmail.trim();
    if (!recipientEmail) {
      toast.error('Renseigne un email de test');
      return;
    }

    const isConfirmed = window.confirm(
      `Lancer le test global des templates vers ${recipientEmail} ?`
    );
    if (!isConfirmed) {
      return;
    }

    setIsSendingSuite(true);
    try {
      const response = await fetch('/api/admin/email-test-suite', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipientEmail,
        }),
      });

      const payload = (await response.json().catch(() => null)) as EmailTestSuitePayload | null;
      if (!response.ok || !payload?.ok) {
        throw new Error((payload as { error?: string } | null)?.error || 'Suite de test email impossible');
      }

      setSuitePayload(payload);
      toast.success(
        `Suite terminee: ${payload.summary.sent} envoyes, ${payload.summary.failed} en echec`
      );
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erreur suite de test');
    } finally {
      setIsSendingSuite(false);
    }
  };

  const handleRunReminderAction = async (action: 'preview' | 'run' | 'test') => {
    const recipientEmail = testRecipientEmail.trim();

    if (action === 'test' && !recipientEmail) {
      toast.error('Renseigne un email de test');
      return;
    }

    if (action === 'run') {
      const isConfirmed = window.confirm(
        'Envoyer maintenant les rappels abonnement dus aux clients concernés ?'
      );
      if (!isConfirmed) {
        return;
      }
    }

    setReminderAction(action);
    try {
      const body: Record<string, unknown> = { action };
      if (action === 'preview') {
        body.dryRun = true;
        if (recipientEmail) {
          body.targetEmail = recipientEmail;
        }
      }
      if (action === 'test') {
        body.recipientEmail = recipientEmail;
      }

      const response = await fetch('/api/admin/subscription-reminders', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const payload = (await response.json().catch(() => null)) as SubscriptionReminderPayload | null;
      if (!response.ok || !payload?.ok) {
        throw new Error(
          (payload as { error?: string } | null)?.error || 'Execution rappel abonnement impossible'
        );
      }

      setReminderPayload(payload);
      setReminderOverview(payload.overview || null);
      setReminderSettingsDraft(payload.overview?.settings || reminderSettingsDraft);

      if (payload.action === 'test') {
        toast.success('Email de rappel test envoye');
      } else if (payload.report) {
        const report = payload.report;
        const sentLabel = report.dryRun ? 'simulations' : 'envois';
        toast.success(
          `${report.stats.eligible} eligibles, ${report.stats.sent} ${sentLabel} (${report.stats.failed} echec)`
        );
      } else {
        toast.success('Rappels abonnement executes');
      }

      await loadData();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erreur rappel abonnement');
    } finally {
      setReminderAction(null);
    }
  };

  const toggleReminderDay = (day: number) => {
    setReminderSettingsDraft((current) => {
      if (!current) {
        return current;
      }

      const hasDay = current.reminderDays.includes(day);
      const nextReminderDays = hasDay
        ? current.reminderDays.filter((entry) => entry !== day)
        : [...current.reminderDays, day].sort((left, right) => right - left);

      return {
        ...current,
        reminderDays: nextReminderDays.length > 0 ? nextReminderDays : [10],
      };
    });
  };

  const handleSaveReminderSettings = async () => {
    if (!reminderSettingsDraft) {
      return;
    }

    setIsSavingReminderSettings(true);
    try {
      const payload = await adminSubscriptionReminderService.saveSettings(
        reminderSettingsDraft
      );
      setReminderOverview(payload.overview || null);
      setReminderSettingsDraft(payload.settings || payload.overview?.settings || null);
      toast.success('Reglages rappels sauvegardes');
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : 'Sauvegarde des reglages impossible'
      );
    } finally {
      setIsSavingReminderSettings(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email & Automations</h2>
          <p className="text-gray-600 mt-2">
            Gere les templates Postmark, les streams transactionnels et tous les flows
            checkout.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setSelectedTemplate(createCustomEmailTemplate(modularTemplate))}
            className="px-4 py-3 rounded-lg bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors"
          >
            Email personnalisé
          </button>
          <button
            onClick={() => setSelectedTemplate(createEmptyTemplate())}
            className="px-4 py-3 rounded-lg bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors"
          >
            Nouveau template avancé
          </button>
          <button
            onClick={() => setSelectedFlow(createEmptyFlow(templates[0]?.alias || ''))}
            className="px-4 py-3 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
          >
            Nouveau flow
          </button>
          <button
            onClick={() => loadData()}
            className="px-4 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Rafraichir
          </button>
          <button
            onClick={handleSyncPostmark}
            disabled={isSyncing || !overview.postmarkConfigured}
            className="px-4 py-3 rounded-lg bg-neon-orange text-white hover:bg-[#FF4500] transition-colors disabled:opacity-60"
          >
            {isSyncing ? 'Synchronisation...' : 'Synchroniser Postmark'}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-emerald-950">
              Email custom sur mesure
            </h3>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-emerald-900">
              Pour créer un email personnalisé, clique sur{' '}
              <strong>Email personnalisé</strong>. On t’ouvre directement une base
              modulaire prête à adapter avec ton sujet, ton HTML, ton texte et tes
              variables client.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedTemplate(createCustomEmailTemplate(modularTemplate))}
              className="rounded-lg bg-neon-orange px-4 py-3 font-semibold text-white transition-colors hover:bg-[#FF4500]"
            >
              Créer un email custom
            </button>
            {modularTemplate && (
              <button
                onClick={() => setSelectedTemplate(modularTemplate)}
                className="rounded-lg bg-white px-4 py-3 font-semibold text-emerald-900 shadow-sm transition-colors hover:bg-emerald-100"
              >
                Modifier la base modulaire
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Connexion Postmark</div>
          <div className="text-lg font-bold mt-2">
            {overview.postmarkConfigured ? 'Connecte' : 'Non configure'}
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Server ID: {overview.serverId || 'non renseigne'}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Templates systeme</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{overview.templatesCount}</div>
          <div className="text-xs text-gray-500 mt-2">
            Distant: {overview.remoteTemplatesCount}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Flows actifs</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">
            {overview.activeFlowsCount}
          </div>
          <div className="text-xs text-gray-500 mt-2">Total: {overview.flowsCount}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Checkouts a surveiller</div>
          <div className="text-3xl font-bold text-amber-600 mt-2">
            {overview.openSessionsCount}
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Commandes payees: {overview.paidSessionsCount}
          </div>
        </div>
      </div>

      {!overview.postmarkConfigured && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-amber-900">
                Configuration Postmark manquante
              </h3>
              <p className="mt-2 text-sm text-amber-800">
                Pour synchroniser les templates, ajoute d&apos;abord le `Server ID`
                et le `Server API Token` dans l&apos;onglet Settings, section
                integrations Mollie & Postmark.
              </p>
            </div>

            {onOpenSettings && (
              <button
                onClick={onOpenSettings}
                className="inline-flex items-center justify-center rounded-lg bg-white px-4 py-3 font-semibold text-amber-900 shadow-sm transition-colors hover:bg-amber-100"
              >
                Ouvrir Settings
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
        <div className="text-sm font-semibold text-gray-700 mb-3">Message Streams</div>
        <div className="flex flex-wrap gap-3">
          {overview.streams.length === 0 ? (
            <span className="text-sm text-gray-500">
              Aucun stream recupere pour le moment.
            </span>
          ) : (
            overview.streams.map((stream) => (
              <span
                key={stream.ID}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm text-gray-700 border border-gray-200"
              >
                <strong>{stream.ID}</strong>
                <span className="text-gray-500">{stream.MessageStreamType}</span>
              </span>
            ))
          )}
        </div>
      </div>

      <section className="space-y-4 rounded-2xl border border-sky-200 bg-sky-50 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-xl font-semibold text-sky-950">Centre de test emails</h3>
            <p className="mt-1 text-sm text-sky-900">
              Verifie tous les templates, les rappels abonnements et le scenario avant echeance.
            </p>
          </div>
          <div className="w-full lg:w-auto lg:min-w-[360px]">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-sky-700">
              Email de test
            </label>
            <input
              value={testRecipientEmail}
              onChange={(event) => setTestRecipientEmail(event.target.value)}
              placeholder="admin@couteausuissedigital.com"
              className="w-full rounded-lg border border-sky-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleRunEmailSuite}
            disabled={isSendingSuite}
            className="rounded-lg bg-neon-orange px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF4500] disabled:opacity-60"
          >
            {isSendingSuite ? 'Test global en cours...' : 'Tester tous les templates'}
          </button>
          <button
            onClick={() => handleRunReminderAction('preview')}
            disabled={reminderAction !== null}
            className="rounded-lg bg-white px-4 py-3 text-sm font-semibold text-sky-900 transition-colors hover:bg-sky-100 disabled:opacity-60"
          >
            {reminderAction === 'preview'
              ? 'Preview rappels...'
              : 'Preview rappels abonnement'}
          </button>
          <button
            onClick={() => handleRunReminderAction('test')}
            disabled={reminderAction !== null}
            className="rounded-lg bg-indigo-100 px-4 py-3 text-sm font-semibold text-indigo-800 transition-colors hover:bg-indigo-200 disabled:opacity-60"
          >
            {reminderAction === 'test'
              ? 'Envoi test rappel...'
              : 'Envoyer 1 rappel test'}
          </button>
          <button
            onClick={() => handleRunReminderAction('run')}
            disabled={reminderAction !== null}
            className="rounded-lg bg-emerald-100 px-4 py-3 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-200 disabled:opacity-60"
          >
            {reminderAction === 'run'
              ? 'Envoi rappels en cours...'
              : 'Envoyer rappels dus maintenant'}
          </button>
        </div>

        <div className="rounded-2xl border border-sky-100 bg-white/80 p-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-sm font-semibold text-sky-950">
                Reglages rappels abonnement
              </div>
              <p className="mt-1 text-sm text-sky-900">
                Active les paliers J-10, J-3 et Jour J, puis definis la fenetre de pilotage de l agenda.
              </p>
            </div>
            <div className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-800">
              Dossiers a risque: {reminderOverview?.stats.atRiskCount ?? 0}
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-sky-700">
                Jours de rappel
              </label>
              <div className="flex flex-wrap gap-2">
                {[10, 3, 0].map((day) => {
                  const active = reminderSettingsDraft?.reminderDays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleReminderDay(day)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                        active
                          ? 'bg-neon-orange text-white'
                          : 'bg-sky-50 text-sky-800 hover:bg-sky-100'
                      }`}
                    >
                      {day === 0 ? 'Jour J' : `J-${day}`}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-sky-700">
                Fenetre agenda
              </label>
              <select
                value={reminderSettingsDraft?.lookaheadDays || 30}
                onChange={(event) =>
                  setReminderSettingsDraft((current) =>
                    current
                      ? {
                          ...current,
                          lookaheadDays: Number(event.target.value || 30),
                        }
                      : current
                  )
                }
                className="w-full rounded-lg border border-sky-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
              >
                <option value={14}>14 jours</option>
                <option value={30}>30 jours</option>
                <option value={45}>45 jours</option>
                <option value={60}>60 jours</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-sky-900">
            <span className="rounded-full bg-sky-100 px-3 py-1 font-semibold text-sky-800">
              J-10: {reminderOverview?.stats.dueInTenDaysCount ?? 0}
            </span>
            <span className="rounded-full bg-indigo-100 px-3 py-1 font-semibold text-indigo-800">
              J-3: {reminderOverview?.stats.dueInThreeDaysCount ?? 0}
            </span>
            <span className="rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-800">
              Jour J: {reminderOverview?.stats.dueTodayCount ?? 0}
            </span>
            <button
              type="button"
              onClick={handleSaveReminderSettings}
              disabled={!reminderSettingsDraft || isSavingReminderSettings}
              className="rounded-lg bg-sky-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-950 disabled:opacity-60"
            >
              {isSavingReminderSettings ? 'Sauvegarde...' : 'Sauvegarder reglages'}
            </button>
          </div>
        </div>

        {suitePayload && (
          <div className="space-y-3 rounded-xl border border-white/70 bg-white p-4">
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
              <span className="rounded-full bg-sky-100 px-3 py-1 font-semibold text-sky-800">
                Destinataire: {suitePayload.recipientEmail}
              </span>
              <span className="rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-700">
                Envoyes: {suitePayload.summary.sent}
              </span>
              <span className="rounded-full bg-red-100 px-3 py-1 font-semibold text-red-700">
                Echecs: {suitePayload.summary.failed}
              </span>
              <span className="rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-700">
                Testes: {suitePayload.summary.tested}
              </span>
            </div>
            <div className="max-h-60 overflow-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Template</th>
                    <th className="px-3 py-2 text-left font-semibold">Alias</th>
                    <th className="px-3 py-2 text-left font-semibold">Statut</th>
                    <th className="px-3 py-2 text-left font-semibold">Info</th>
                  </tr>
                </thead>
                <tbody>
                  {suitePayload.results.map((entry) => (
                    <tr key={`${entry.alias}_${entry.status}`} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-900">{entry.name}</td>
                      <td className="px-3 py-2 text-gray-600">{entry.alias}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            entry.status === 'sent'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {entry.messageId || entry.error || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {reminderPayload && (
          <div className="space-y-3 rounded-xl border border-white/70 bg-white p-4">
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
              <span className="rounded-full bg-indigo-100 px-3 py-1 font-semibold text-indigo-800">
                Action: {reminderPayload.action}
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700">
                Template: {reminderPayload.report?.templateAlias || reminderPayload.result?.templateAlias || '-'}
              </span>
            </div>

            {reminderPayload.result && (
              <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-900">
                Test envoye a {reminderPayload.result.recipientEmail} | Abonnement{' '}
                {reminderPayload.result.subscriptionNumber || 'N/A'} | Message ID:{' '}
                {reminderPayload.result.messageId || 'N/A'}
              </div>
            )}

            {reminderPayload.report && (
              <>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-700">
                    Jours rappels: {reminderPayload.report.reminderDays.join(', ')}
                  </span>
                  <span className="rounded-full bg-sky-100 px-3 py-1 font-semibold text-sky-800">
                    Verifies: {reminderPayload.report.stats.checked}
                  </span>
                  <span className="rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-800">
                    Eligibles: {reminderPayload.report.stats.eligible}
                  </span>
                  <span className="rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-700">
                    Envoyes: {reminderPayload.report.stats.sent}
                  </span>
                  <span className="rounded-full bg-red-100 px-3 py-1 font-semibold text-red-700">
                    Echecs: {reminderPayload.report.stats.failed}
                  </span>
                </div>
                <div className="max-h-56 overflow-auto rounded-lg border border-gray-100">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Statut</th>
                        <th className="px-3 py-2 text-left font-semibold">Abonnement</th>
                        <th className="px-3 py-2 text-left font-semibold">Email</th>
                        <th className="px-3 py-2 text-left font-semibold">Echeance</th>
                        <th className="px-3 py-2 text-left font-semibold">Info</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reminderPayload.report.results.map((entry, index) => (
                        <tr key={`${entry.subscriptionNumber || 'sub'}_${entry.status}_${index}`} className="border-t border-gray-100">
                          <td className="px-3 py-2 text-gray-700">{entry.status}</td>
                          <td className="px-3 py-2 text-gray-900">
                            {entry.subscriptionNumber || '-'}
                          </td>
                          <td className="px-3 py-2 text-gray-700">{entry.recipientEmail || '-'}</td>
                          <td className="px-3 py-2 text-gray-700">
                            {entry.targetDate || '-'} ({entry.daysBefore ?? '-'}j)
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {entry.messageId || entry.error || entry.templateAlias || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </section>

      {isLoading ? (
        <div className="py-16 text-center text-gray-600">Chargement des emails...</div>
      ) : (
        <>
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Historique des emails envoyes</h3>
              <span className="text-sm text-gray-500">Derniers {history.length} envois</span>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              {history.length === 0 ? (
                <div className="p-6 text-sm text-gray-500">
                  Aucun email envoye pour le moment.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Date</th>
                        <th className="px-4 py-3 text-left font-semibold">Destinataire</th>
                        <th className="px-4 py-3 text-left font-semibold">Template / Sujet</th>
                        <th className="px-4 py-3 text-left font-semibold">Stream / Tag</th>
                        <th className="px-4 py-3 text-left font-semibold">Statut</th>
                        <th className="px-4 py-3 text-left font-semibold">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((entry, index) => (
                        <tr key={`${entry.id || entry.messageId || 'mail'}_${index}`} className="border-t border-gray-100">
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                            {entry.submittedAt
                              ? new Date(entry.submittedAt).toLocaleString('fr-FR')
                              : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-gray-900">{entry.to || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">
                              {entry.templateAlias || 'sans template'}
                            </div>
                            <div className="text-xs text-gray-500">{entry.subject || '-'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-gray-800">{entry.messageStream || '-'}</div>
                            <div className="text-xs text-gray-500">{entry.tag || '-'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${emailStatusBadge(
                                entry.status || ''
                              )}`}
                            >
                              {entry.status || 'unknown'}
                            </span>
                            {entry.error && (
                              <div className="mt-1 text-xs text-red-600 max-w-xs truncate" title={entry.error}>
                                {entry.error}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{entry.source || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Templates</h3>
              <span className="text-sm text-gray-500">
                Stream par defaut: {overview.defaultMessageStream}
              </span>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {templates.map((template) => (
                <motion.div
                  key={template.templateKey}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">{template.name}</h4>
                      <div className="text-sm text-gray-500">{template.alias}</div>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        template.active
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {template.active ? 'Actif' : 'Desactive'}
                    </span>
                  </div>

                  <div className="text-sm text-gray-700 mb-3">{template.subject}</div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-500 mb-4">
                    <div>Stream: {template.messageStream}</div>
                    <div>
                      Sync: {template.lastSyncedAt ? new Date(template.lastSyncedAt).toLocaleString('fr-FR') : 'Jamais'}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setSelectedTemplate(template)}
                      className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleOpenPostmarkTemplate(template)}
                      className="px-4 py-2 rounded-lg bg-indigo-100 text-indigo-800 hover:bg-indigo-200 transition-colors"
                    >
                      Ouvrir Postmark
                    </button>
                    <button
                      onClick={() => handleSendTest(template.alias)}
                      className="px-4 py-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                    >
                      Test send
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-xl font-semibold">Flows</h3>
            <div className="grid gap-4 lg:grid-cols-2">
              {flows.map((flow) => (
                <motion.div
                  key={flow.flowKey}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">{flow.name}</h4>
                      <div className="text-sm text-gray-500">{flow.description}</div>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        flow.enabled
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {flow.enabled ? 'Actif' : 'Pause'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 mb-4">
                    <div>Declencheur: {triggerLabels[flow.triggerType]}</div>
                    <div>Destinataire: {recipientLabels[flow.recipientType]}</div>
                    <div>Delai: {flow.delayMinutes} min</div>
                    <div>Max sends: {flow.maxSends}</div>
                    <div>Template: {flow.templateAlias}</div>
                    <div>Stream: {flow.messageStream}</div>
                  </div>

                  <button
                    onClick={() => setSelectedFlow(flow)}
                    className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    Modifier
                  </button>
                </motion.div>
              ))}
            </div>
          </section>
        </>
      )}

      {selectedTemplate && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedTemplate(null)}
        >
          <div
            className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold">
                  {selectedTemplate.id ? 'Éditer le template' : 'Créer un email / template'}
                </h3>
                {!selectedTemplate.id && (
                  <p className="mt-1 text-sm text-gray-500">
                    Utilise cette fenêtre pour créer un email custom prêt à synchroniser avec
                    Postmark.
                  </p>
                )}
              </div>
              <button
                onClick={() => setSelectedTemplate(null)}
                aria-label="Fermer la fenêtre template"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-700 transition-colors hover:bg-gray-200"
              >
                ×
              </button>
            </div>
            <div className="grid gap-4 overflow-y-auto p-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template key
                  </label>
                  <input
                    value={selectedTemplate.templateKey}
                    readOnly={Boolean(selectedTemplate.id)}
                    onChange={(event) =>
                      setSelectedTemplate({
                        ...selectedTemplate,
                        templateKey: event.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none read-only:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input
                    value={selectedTemplate.name}
                    onChange={(event) =>
                      setSelectedTemplate({ ...selectedTemplate, name: event.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alias</label>
                  <input
                    value={selectedTemplate.alias}
                    onChange={(event) =>
                      setSelectedTemplate({ ...selectedTemplate, alias: event.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sujet</label>
                  <input
                    value={selectedTemplate.subject}
                    onChange={(event) =>
                      setSelectedTemplate({
                        ...selectedTemplate,
                        subject: event.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message Stream
                    </label>
                    <input
                      value={selectedTemplate.messageStream}
                      onChange={(event) =>
                        setSelectedTemplate({
                          ...selectedTemplate,
                          messageStream: event.target.value,
                        })
                      }
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From email
                    </label>
                    <input
                      value={selectedTemplate.fromEmail || ''}
                      onChange={(event) =>
                        setSelectedTemplate({
                          ...selectedTemplate,
                          fromEmail: event.target.value,
                        })
                      }
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reply-to
                    </label>
                    <input
                      value={selectedTemplate.replyTo || ''}
                      onChange={(event) =>
                        setSelectedTemplate({
                          ...selectedTemplate,
                          replyTo: event.target.value,
                        })
                      }
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-3 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={selectedTemplate.active}
                        onChange={(event) =>
                          setSelectedTemplate({
                            ...selectedTemplate,
                            active: event.target.checked,
                          })
                        }
                      />
                      Template actif
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sample model JSON
                </label>
                <textarea
                  value={JSON.stringify(selectedTemplate.sampleModel, null, 2)}
                  onChange={(event) => {
                    try {
                      const sampleModel = JSON.parse(event.target.value);
                      setSelectedTemplate({ ...selectedTemplate, sampleModel });
                    } catch {
                      // Let the user continue editing invalid JSON temporarily.
                    }
                  }}
                  rows={12}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none font-mono text-sm"
                />
              </div>

              <div className="lg:col-span-2 grid gap-4 lg:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HTML body
                  </label>
                  <textarea
                    value={selectedTemplate.htmlBody}
                    onChange={(event) =>
                      setSelectedTemplate({
                        ...selectedTemplate,
                        htmlBody: event.target.value,
                      })
                    }
                    rows={16}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Text body
                  </label>
                  <textarea
                    value={selectedTemplate.textBody}
                    onChange={(event) =>
                      setSelectedTemplate({
                        ...selectedTemplate,
                        textBody: event.target.value,
                      })
                    }
                    rows={16}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none font-mono text-sm"
                  />
                </div>
              </div>

              <div className="lg:col-span-2 px-0 pt-2 flex items-center justify-end gap-3">
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="px-4 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveTemplate}
                  className="px-4 py-3 rounded-lg bg-neon-orange text-white hover:bg-[#FF4500] transition-colors"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedFlow && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedFlow(null)}
        >
          <div
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
              <h3 className="text-xl font-semibold">Editer le flow</h3>
              <button
                onClick={() => setSelectedFlow(null)}
                aria-label="Fermer la fenêtre flow"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-700 transition-colors hover:bg-gray-200"
              >
                ×
              </button>
            </div>
            <div className="grid gap-4 overflow-y-auto p-6 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flow key</label>
                <input
                  value={selectedFlow.flowKey}
                  readOnly={Boolean(selectedFlow.id)}
                  onChange={(event) =>
                    setSelectedFlow({ ...selectedFlow, flowKey: event.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none read-only:bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  value={selectedFlow.name}
                  onChange={(event) =>
                    setSelectedFlow({ ...selectedFlow, name: event.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-3 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedFlow.enabled}
                    onChange={(event) =>
                      setSelectedFlow({ ...selectedFlow, enabled: event.target.checked })
                    }
                  />
                  Flow actif
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={selectedFlow.description}
                  onChange={(event) =>
                    setSelectedFlow({ ...selectedFlow, description: event.target.value })
                  }
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Declencheur
                </label>
                <select
                  value={selectedFlow.triggerType}
                  onChange={(event) =>
                    setSelectedFlow({
                      ...selectedFlow,
                      triggerType: event.target.value as EmailFlowTrigger,
                    })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
                >
                  {Object.entries(triggerLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destinataire
                </label>
                <select
                  value={selectedFlow.recipientType}
                  onChange={(event) =>
                    setSelectedFlow({
                      ...selectedFlow,
                      recipientType: event.target.value as EmailFlowRecipient,
                    })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
                >
                  {Object.entries(recipientLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>
                <select
                  value={selectedFlow.templateAlias}
                  onChange={(event) =>
                    setSelectedFlow({
                      ...selectedFlow,
                      templateAlias: event.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
                >
                  {templates.map((template) => (
                    <option key={template.alias} value={template.alias}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message Stream
                </label>
                <input
                  value={selectedFlow.messageStream}
                  onChange={(event) =>
                    setSelectedFlow({
                      ...selectedFlow,
                      messageStream: event.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delai (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={selectedFlow.delayMinutes}
                  onChange={(event) =>
                    setSelectedFlow({
                      ...selectedFlow,
                      delayMinutes: Number(event.target.value || 0),
                    })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre max d envois
                </label>
                <input
                  type="number"
                  min="1"
                  value={selectedFlow.maxSends}
                  onChange={(event) =>
                    setSelectedFlow({
                      ...selectedFlow,
                      maxSends: Number(event.target.value || 1),
                    })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                />
              </div>

              <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setSelectedFlow(null)}
                  className="px-4 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveFlow}
                  className="px-4 py-3 rounded-lg bg-neon-orange text-white hover:bg-[#FF4500] transition-colors"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailManager;
